import React from 'react';
import { ConfigProvider } from 'antd';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useTheme } from '../../theme/ThemeContext';
import { fixUnclosedColorSpans, markdownComponents } from '../../shared/markdownComponents';

export interface TakeawayTextSegment {
    /** The text content */
    text: string;
    /** Whether this segment should be bold/highlighted */
    isBold?: boolean;
}

export interface TakeawayItem {
    /** Array of text segments with optional bold formatting */
    segments: TakeawayTextSegment[];
}

export interface KeyTakeawaysProps {
    /** Title of the takeaways section */
    title?: string;
    /** Array of takeaway items */
    items?: TakeawayItem[];
    /** When true, render item text as markdown/HTML (e.g. for colored spans from API) */
    renderAsMarkdown?: boolean;
    /** Custom markdown components (e.g. agenticMarkdownComponents for agentic UI styling) */
    customMarkdownComponents?: Components;
    /** Custom icon to display (defaults to StarFilled) */
    icon?: React.ReactNode;
    /** Data test attribute */
    'data-test'?: string;
    /** Data test ID attribute */
    'data-test-id'?: string;
}

const KeyTakeaways: React.FC<KeyTakeawaysProps> = (props) => {
    const {
        items = [],
        renderAsMarkdown = false,
        customMarkdownComponents,
    } = props;

    const theme = useTheme();
    const mdComponents = customMarkdownComponents ?? markdownComponents;

    const renderTakeawayText = (segments: TakeawayTextSegment[]) => {
        return segments.map((segment, index) => (
            <span key={index} className={segment.isBold ? 'font-medium text-[#1f1f1f]' : 'text-[#4a5565]'}>
                {segment.text}
            </span>
        ));
    };

    const renderItemContent = (item: TakeawayItem) => {
        const text = fixUnclosedColorSpans(item.segments.map((s) => s.text).join(''));
        if (renderAsMarkdown && text) {
            return (
                <div className="font-['Open_Sans'] text-[16px] leading-[22px] text-[#4a5565] markdown-content">
                    <ReactMarkdown
                        rehypePlugins={[rehypeRaw]}
                        remarkPlugins={[remarkGfm]}
                        remarkRehypeOptions={{ allowDangerousHtml: true }}
                        components={mdComponents}
                    >
                        {text}
                    </ReactMarkdown>
                </div>
            );
        }
        return renderTakeawayText(item.segments);
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className="bg-[#fafafa] flex flex-col items-start px-3 py-2 rounded-lg w-full"
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-key-takeaways
            >
                <div className="flex flex-col gap-2.5 items-start w-full">
                    {/* Header
                    <div className="flex items-center gap-2 w-full">
                        <div className="bg-white flex items-center justify-center p-2 rounded size-9">
                            {icon || <Stars02 size={16} color="#5f49bc" />}
                        </div>
                        <p className="font-sans font-medium text-[16px] leading-normal text-[#1f1f1f] m-0">{title}</p>
                    </div> */}

                    {/* Takeaway Items */}
                    {items.map((item, index) => (
                        <div key={index} className="flex items-center w-full">
                            <ul className="font-sans text-[16px] leading-[22px] m-0 pl-5 list-disc">
                                <li>{renderItemContent(item)}</li>
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </ConfigProvider>
    );
};

export default KeyTakeaways;
